import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import AntdModal, { ModalProps } from "antd/lib/modal";

export interface ModalRef {
    open: () => void;
    close: () => void;
}

type Props = Omit<ModalProps, "open"> &
    Partial<{
        beforeClose: () => void;
        beforeOpen: () => void;
        afterClose: () => void;
        afterOpen: () => void;
        disableAnimation?: boolean;
    }>;

/**
 * Use footer={null} if you dont need default buttons
 */

const Modal = forwardRef<ModalRef, Props>((props, ref) => {
    const {
        beforeOpen,
        beforeClose,
        afterOpen,
        afterClose,
        onCancel,
        onOk,
        disableAnimation,
        ...rest
    } = props;
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        open: () => {
            beforeOpen?.();
            setIsOpen(true);
            afterOpen?.();
        },
        close: () => {
            beforeClose?.();
            setIsOpen(false);
            afterClose?.();
        },
    }));

    const cancelCallback = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            beforeClose?.();
            onCancel?.(event);
            setIsOpen(false);
            afterClose?.();
        },
        [beforeClose, onCancel, afterClose],
    );

    const okCallback = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            beforeClose?.();
            onOk?.(event);
            setIsOpen(false);
            afterClose?.();
        },
        [beforeClose, onOk, afterClose],
    );

    return (
        <AntdModal
            {...rest}
            open={isOpen}
            onCancel={cancelCallback}
            onOk={okCallback}
            {...(disableAnimation && {
                modalRender: (modal) => (
                    <div className="!transition-none !duration-0">{modal}</div>
                ),
                animation: false,
                transitionName: "",
            })}
        />
    );
});

Modal.displayName = "Modal";

export default Modal;
